import React, { ReactNode } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';

interface RoleBasedComponentProps {
  resource?: string;
  requiredPermission?: 'create' | 'edit' | 'delete' | 'view';
  fallback?: ReactNode;
  children: ReactNode;
  tooltip?: string;
}

/**
 * A component that conditionally renders its children based on user permissions
 * 
 * @param resource - The resource type being accessed (e.g., 'games', 'questions')
 * @param requiredPermission - The permission required ('create', 'edit', 'delete', 'view')
 * @param fallback - Optional component to render if permission is denied
 * @param children - The components to render if permission is granted
 * @param tooltip - Optional tooltip text to show when hovering over disabled elements
 */
export const RoleBasedComponent: React.FC<RoleBasedComponentProps> = ({
  resource,
  requiredPermission,
  fallback = null,
  children,
  tooltip
}) => {
  const permissions = usePermissions();
  
  // Check if the user has the required permission for the resource
  const hasPermission = (): boolean => {
    if (!resource || !requiredPermission) return true;
    
    switch (requiredPermission) {
      case 'create':
        return permissions.canCreate(resource);
      case 'edit':
        return permissions.canEdit(resource);
      case 'delete':
        return permissions.canDelete(resource);
      case 'view':
        return permissions.canView(resource);
      default:
        return false;
    }
  };
  
  // If user has permission, render children, otherwise render fallback
  if (hasPermission()) {
    return <>{children}</>;
  }
  
  // If tooltip is provided and fallback is not, render a disabled version with tooltip
  if (tooltip && !fallback) {
    return (
      <div className="relative inline-block cursor-not-allowed opacity-60" title={tooltip}>
        {children}
      </div>
    );
  }
  
  // Otherwise render the fallback or null
  return <>{fallback}</>;
};

/**
 * A button component that is disabled based on user permissions
 */
export const RoleBasedButton: React.FC<RoleBasedComponentProps & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  resource,
  requiredPermission,
  children,
  tooltip,
  ...buttonProps
}) => {
  const permissions = usePermissions();
  
  // Check if the user has the required permission for the resource
  const hasPermission = (): boolean => {
    if (!resource || !requiredPermission) return true;
    
    switch (requiredPermission) {
      case 'create':
        return permissions.canCreate(resource);
      case 'edit':
        return permissions.canEdit(resource);
      case 'delete':
        return permissions.canDelete(resource);
      case 'view':
        return permissions.canView(resource);
      default:
        return false;
    }
  };
  
  return (
    <button
      {...buttonProps}
      disabled={!hasPermission() || buttonProps.disabled}
      title={!hasPermission() ? tooltip : buttonProps.title}
      className={`${buttonProps.className || ''} ${!hasPermission() ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

/**
 * A component that only renders for specific user roles
 */
export const RoleRestricted: React.FC<{
  allowedRoles: string[];
  fallback?: ReactNode;
  children: ReactNode;
}> = ({ allowedRoles, fallback = null, children }) => {
  const { userRole } = usePermissions();
  
  if (allowedRoles.includes(userRole)) {
    return <>{children}</>;
  }
  
  return <>{fallback}</>;
};
